"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { signIn, useSession } from "next-auth/react";
import {
  initializeTeams,
  getTeamsToken,
  notifyAppLoaded,
  isTeamsHostHeuristic,
  isInTeamsHost,
} from "@/lib/teams";
import { trackPlatformLogin } from "@/utils/platformTrackerApi";

/**
 * Shared storage key used by both this context and src/lib/api.ts.
 * Exported so api.ts can import it without a magic string.
 */
export const TREK_TOKEN_KEY = "trek_auth_token";

/**
 * Guard against an infinite signIn → reload loop when the session cookie
 * never persists (e.g. SameSite/iframe issue not yet fixed in production).
 * Set after the first signIn attempt; cleared once a valid session is seen.
 */
const TEAMS_SIGNIN_ATTEMPTED_KEY = "trek_teams_signin_attempted";

/** Per-tab marker: last user email reported to GET /api/v1/platform-tracker after login. */
const PLATFORM_TRACKER_DONE_KEY = "trek_platform_tracker_done";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TeamsUser {
  name: string;
  upn: string;
}

interface TeamsAuthState {
  inTeams: boolean;
  loading: boolean;
  error: string | null;
  token: string | null;
  user: TeamsUser | null;
}

interface TeamsAuthContextValue extends TeamsAuthState {
  getToken: () => string | null;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const TeamsAuthContext = createContext<TeamsAuthContextValue | null>(null);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseUserFromToken(token: string): TeamsUser {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      name: payload.name ?? "",
      upn: payload.preferred_username ?? payload.upn ?? "",
    };
  } catch {
    return { name: "", upn: "" };
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function TeamsAuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status: sessionStatus } = useSession();
  const [state, setState] = useState<TeamsAuthState>(() => ({
    inTeams: typeof window !== "undefined" ? isTeamsHostHeuristic() : false,
    loading: true,
    error: null,
    token: null,
    user: null,
  }));

  useEffect(() => {
    // Wait until next-auth has determined whether a session cookie exists.
    if (sessionStatus === "loading") return;

    let cancelled = false;

    (async () => {
      await initializeTeams();
      const inTeams = isInTeamsHost();

      if (!inTeams) {
        if (!cancelled) setState((s) => ({ ...s, inTeams: false, loading: false }));
        return;
      }

      try {
        // Get the raw Teams SSO token — no backend call needed
        const token = await getTeamsToken();
        const user = parseUserFromToken(token);
        sessionStorage.setItem(TREK_TOKEN_KEY, token);

        // If next-auth already has a session for this user, just hydrate local state.
        // Avoids a sign-in / reload loop on subsequent renders.
        if (sessionStatus === "authenticated") {
          sessionStorage.removeItem(TEAMS_SIGNIN_ATTEMPTED_KEY);
          if (!cancelled) {
            setState({ inTeams: true, loading: false, error: null, token, user });
            notifyAppLoaded();
          }
          return;
        }

        // Loop guard: if we already tried signIn this browsing session and it
        // didn't stick, surface an error instead of looping forever. Most likely
        // cause: session cookie blocked in the iframe (SameSite / 3rd-party
        // cookie policy). Fixing the [...nextauth].ts cookie config resolves it.
        if (sessionStorage.getItem(TEAMS_SIGNIN_ATTEMPTED_KEY) === "1") {
          throw new Error(
            "Teams SSO sign-in did not establish a NextAuth session. " +
            "Likely cause: the session cookie was blocked by the browser's " +
            "third-party cookie policy. Check that cookies use SameSite=None; Secure."
          );
        }

        // No next-auth session yet — bridge the Teams SSO token through the
        // "teams-sso" CredentialsProvider so useSession()/getServerSession()
        // see a valid session and pages stop redirecting to /api/auth/signin.
        sessionStorage.setItem(TEAMS_SIGNIN_ATTEMPTED_KEY, "1");
        const result = await signIn("teams-sso", {
          teamsToken: token,
          redirect: false,
        });
        if (result?.error) {
          throw new Error(`teams-sso sign-in failed: ${result.error}`);
        }
        if (cancelled) return;

        // Reload so SessionProvider re-fetches the session cookie and any
        // auth-gated page redirects re-evaluate. NextAuth's signin page will
        // forward an authenticated user to the callbackUrl on its own.
        window.location.reload();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Teams SSO failed";
        if (!cancelled) {
          setState({ inTeams: true, loading: false, error: message, token: null, user: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (sessionStatus === "unauthenticated") {
      sessionStorage.removeItem(PLATFORM_TRACKER_DONE_KEY);
      return;
    }

    if (sessionStatus !== "authenticated") return;

    const email = session?.user?.email;
    if (!email) return;

    if (sessionStorage.getItem(PLATFORM_TRACKER_DONE_KEY) === email) return;

    const accessToken = (session as { accessToken?: string } | null)?.accessToken;
    const platform = state.inTeams ? "teams" : "web";

    void trackPlatformLogin(platform, accessToken)
      .then(() => {
        sessionStorage.setItem(PLATFORM_TRACKER_DONE_KEY, email);
      })
      .catch((err) => {
        console.warn("[TeamsAuthProvider] platform-tracker failed:", err);
      });
  }, [sessionStatus, session, state.inTeams]);

  const getToken = useCallback(
    () => state.token ?? sessionStorage.getItem(TREK_TOKEN_KEY),
    [state.token]
  );

  return (
    <TeamsAuthContext.Provider value={{ ...state, getToken }}>
      {children}
    </TeamsAuthContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useTeamsAuth(): TeamsAuthContextValue {
  const ctx = useContext(TeamsAuthContext);
  if (!ctx) throw new Error("useTeamsAuth must be used inside TeamsAuthProvider");
  return ctx;
}
